import { useState, useEffect } from 'react';
import { TrendingUp, ArrowUpRight, Calendar, Download, ArrowDownRight, Users, CreditCard, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { API_URL as API } from '../../config/api';

function parseDateParts(dateVal) {
  if (!dateVal) return null;
  if (typeof dateVal === 'string') {
    const clean = dateVal.trim();
    const isoMatch = clean.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})/);
    if (isoMatch) {
      return {
        year: parseInt(isoMatch[1], 10),
        month: parseInt(isoMatch[2], 10),
        day: parseInt(isoMatch[3], 10),
      };
    }
    const dmyMatch = clean.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
    if (dmyMatch) {
      return {
        year: parseInt(dmyMatch[3], 10),
        month: parseInt(dmyMatch[2], 10),
        day: parseInt(dmyMatch[1], 10),
      };
    }
  }
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return null;
  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
  };
}

function getBookingDateParts(b) {
  return parseDateParts(b?.date || b?.booking_date || b?.createdAt || b?.created_at);
}

function getBookingEarnings(b) {
  if (!b) return 0;
  if (typeof b.partner_payout === 'number' && b.partner_payout > 0) return b.partner_payout;
  if (typeof b.partnerPayout === 'number' && b.partnerPayout > 0) return b.partnerPayout;
  if (typeof b.payout_amount === 'number' && b.payout_amount > 0) return b.payout_amount;
  const base = Number(b.base_amount || b.court_amount || b.total_price || b.amount || 0);
  const fee = Number(b.partner_platform_fee || 0);
  const gst = Number(b.partner_gst || 0);
  if (base > 0) {
    return Math.max(0, fee > 0 ? (base - fee - gst) : Math.round(base * 0.95));
  }
  return 0;
}

function isBookingActive(b) {
  if (!b) return false;
  const st = (b?.status || '').toLowerCase().trim();
  if (!st) return true;
  if (['cancelled', 'refunded', 'rejected', 'failed'].includes(st)) return false;
  return true;
}

export default function PartnerEarnings({ data: propData }) {
  const { token } = useAuth();
  const [period, setPeriod] = useState('all'); // default to 'all' so data is immediately visible
  const [internalData, setInternalData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchEarningsData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/owner/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInternalData(res.data);
    } catch (err) {
      console.error('Failed to fetch partner dashboard earnings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!propData?.bookings?.length) {
      fetchEarningsData();
    }
  }, [propData, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeData = (propData?.bookings?.length ? propData : internalData) || propData || {};
  const allBookings = activeData?.bookings || [];
  
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;
  const currentDay = today.getDate();

  // Filter bookings based on selected period
  const filterByPeriod = (b) => {
    if (period === 'all') return true;
    const parts = getBookingDateParts(b);
    if (!parts) return true;

    if (period === 'today') {
      return parts.year === currentYear && parts.month === currentMonth && parts.day === currentDay;
    }
    if (period === 'this_month') {
      return parts.year === currentYear && parts.month === currentMonth;
    }
    if (period === 'this_year') {
      return parts.year === currentYear;
    }
    return true;
  };

  const periodBookings = allBookings.filter(filterByPeriod);
  const confirmedBookings = periodBookings.filter(isBookingActive);
  const cancelledBookings = periodBookings.filter(b => (b?.status || '').toLowerCase() === 'cancelled');

  const totalEarnings = confirmedBookings.reduce((sum, b) => sum + getBookingEarnings(b), 0);
  const totalRevenue = confirmedBookings.reduce((sum, b) => sum + (Number(b.total_price) || getBookingEarnings(b)), 0);
  const avgBookingEarnings = confirmedBookings.length ? Math.round(totalEarnings / confirmedBookings.length) : 0;
  const totalPlayers = new Set(confirmedBookings.map(b => b.user_id?._id || b.user_id || b.user_name || b.name).filter(Boolean)).size;
  const platformFees = Math.max(totalRevenue - totalEarnings, 0);
  const cancelledValue = cancelledBookings.reduce((sum, b) => sum + getBookingEarnings(b), 0);

  // Dynamic Chart Buckets based on selected period
  let chartBuckets = [];
  if (period === 'today') {
    chartBuckets = [
      { label: 'Morning (6-12)', revenue: 0 },
      { label: 'Afternoon (12-5)', revenue: 0 },
      { label: 'Evening (5-9)', revenue: 0 },
      { label: 'Night (9-12)', revenue: 0 },
    ];
    confirmedBookings.forEach(b => {
      const slotStr = String(b.time_slot || (Array.isArray(b.time_slots) ? b.time_slots[0] : '') || '');
      const hourMatch = slotStr.match(/^(\d{1,2})/);
      const hour = hourMatch ? parseInt(hourMatch[1], 10) : -1;
      const earnings = getBookingEarnings(b);

      if (hour >= 6 && hour < 12) chartBuckets[0].revenue += earnings;
      else if (hour >= 12 && hour < 17) chartBuckets[1].revenue += earnings;
      else if (hour >= 17 && hour < 21) chartBuckets[2].revenue += earnings;
      else chartBuckets[3].revenue += earnings;
    });
  } else if (period === 'this_month') {
    chartBuckets = [
      { label: 'W1 (1-7)', revenue: 0 },
      { label: 'W2 (8-14)', revenue: 0 },
      { label: 'W3 (15-21)', revenue: 0 },
      { label: 'W4 (22-28)', revenue: 0 },
      { label: 'W5 (29-31)', revenue: 0 },
    ];
    confirmedBookings.forEach(b => {
      const parts = getBookingDateParts(b);
      const day = parts?.day || 1;
      const earnings = getBookingEarnings(b);
      if (day <= 7) chartBuckets[0].revenue += earnings;
      else if (day <= 14) chartBuckets[1].revenue += earnings;
      else if (day <= 21) chartBuckets[2].revenue += earnings;
      else if (day <= 28) chartBuckets[3].revenue += earnings;
      else chartBuckets[4].revenue += earnings;
    });
  } else if (period === 'this_year') {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    chartBuckets = monthNames.map((m, idx) => ({ label: m, revenue: 0, monthNum: idx + 1 }));
    confirmedBookings.forEach(b => {
      const parts = getBookingDateParts(b);
      const mNum = parts?.month || 1;
      const earnings = getBookingEarnings(b);
      if (mNum >= 1 && mNum <= 12) {
        chartBuckets[mNum - 1].revenue += earnings;
      }
    });
  } else {
    // All Time (Bi-monthly distribution)
    chartBuckets = [
      { label: 'Jan-Feb', revenue: 0 },
      { label: 'Mar-Apr', revenue: 0 },
      { label: 'May-Jun', revenue: 0 },
      { label: 'Jul-Aug', revenue: 0 },
      { label: 'Sep-Oct', revenue: 0 },
      { label: 'Nov-Dec', revenue: 0 },
    ];
    confirmedBookings.forEach(b => {
      const parts = getBookingDateParts(b);
      const mNum = parts?.month || 1;
      const earnings = getBookingEarnings(b);
      const bucketIdx = Math.min(Math.floor((mNum - 1) / 2), 5);
      chartBuckets[bucketIdx].revenue += earnings;
    });
  }

  const maxRevenue = Math.max(...chartBuckets.map(b => b.revenue), 1);
  const chartHeights = chartBuckets.map(b => Math.max((b.revenue / maxRevenue) * 100, 6));

  const breakdown = [
    { label: 'Partner Net Earnings', value: totalEarnings, color: '#CEF17B' },
    { label: 'Platform Fees & Deductions', value: platformFees, color: '#3b82f6' },
    { label: 'Cancelled Bookings', value: cancelledValue, color: '#8b5cf6' },
  ];
  const totalBreakdown = Math.max(...breakdown.map(item => item.value), 1);

  const nextPayoutDate = new Date();
  nextPayoutDate.setDate(nextPayoutDate.getDate() + ((5 - nextPayoutDate.getDay() + 7) % 7 || 7));

  const stats = [
    { label: 'Total Earnings', value: `Rs.${Math.round(totalEarnings).toLocaleString('en-IN')}`, change: `${confirmedBookings.length} bookings`, isUp: true, icon: <TrendingUp size={22} />, color: '#CEF17B' },
    { label: 'Avg Booking Payout', value: `Rs.${Math.round(avgBookingEarnings).toLocaleString('en-IN')}`, change: `${confirmedBookings.length} paid`, isUp: true, icon: <CreditCard size={22} />, color: '#3b82f6' },
    { label: 'Total Players', value: totalPlayers.toLocaleString('en-IN'), change: `${periodBookings.length} total`, isUp: true, icon: <Users size={22} />, color: '#8b5cf6' },
    { label: 'Platform Fees', value: `Rs.${Math.round(platformFees).toLocaleString('en-IN')}`, change: 'Platform Commission', isUp: false, icon: <ArrowDownRight size={22} />, color: '#ef4444' },
  ];

  const filterOptions = [
    { id: 'today', label: 'Today' },
    { id: 'this_month', label: 'This Month' },
    { id: 'this_year', label: 'This Year' },
    { id: 'all', label: 'All Time' },
  ];

  return (
    <div style={{ animation: 'fadeIn 0.5s ease-out' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ fontSize: '1.8rem', fontWeight: '800', color: '#161616', marginBottom: '8px' }}>Earnings & Analytics</h2>
          <p style={{ color: '#374151', fontWeight: '500' }}>Net partner earnings and performance from your confirmed bookings</p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Period Filter Tabs */}
          <div style={{ display: 'flex', background: '#F4F6F3', borderRadius: '12px', padding: '4px', border: '1px solid #E5E7EB' }}>
            {filterOptions.map((opt) => (
              <button
                key={opt.id}
                onClick={() => setPeriod(opt.id)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '10px',
                  fontSize: '0.85rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  border: 'none',
                  background: period === opt.id ? '#084734' : 'transparent',
                  color: period === opt.id ? '#CEF17B' : '#4B5563',
                  transition: 'all 0.2s',
                  boxShadow: period === opt.id ? '0 2px 8px rgba(8,71,52,0.2)' : 'none',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <button onClick={fetchEarningsData} style={btnSecondary} title="Refresh data">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>

          <button
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/exports/earnings/csv`, {
                  headers: { Authorization: `Bearer ${token}` },
                  responseType: 'blob',
                });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `earnings-${Date.now()}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
              } catch {
                alert('Export failed. Please try again.');
              }
            }}
            style={btnPrimary}
          >
            <Download size={18} /> Export Data
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.2rem', marginBottom: '2.5rem' }}>
        {stats.map((s, i) => (
          <div key={i} style={statCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: `${s.color}15`, color: s.color === '#CEF17B' ? '#084734' : s.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {s.icon}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: s.isUp ? '#084734' : '#ef4444', fontSize: '0.75rem', fontWeight: '800', background: s.isUp ? '#DCEFB8' : '#fff1f2', padding: '4px 8px', borderRadius: '6px' }}>
                {s.isUp ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />} {s.change}
              </div>
            </div>
            <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>{s.label}</div>
            <div style={{ fontSize: '1.6rem', fontWeight: '900', color: '#161616' }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Charts & Breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem' }}>
        <div style={{ background: 'white', padding: '2rem', borderRadius: '24px', border: '1.5px solid #EEF2E6' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
            <div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '800', margin: 0 }}>Earnings Trend</h3>
              <p style={{ fontSize: '0.8rem', color: '#6B7280', margin: '4px 0 0' }}>
                {period === 'today' ? "Today's Time Slots" : period === 'this_month' ? 'Weekly Distribution (This Month)' : period === 'this_year' ? 'Monthly Distribution (This Year)' : 'All Time Breakdown'}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', fontWeight: '700', color: '#084734', background: '#DCEFB8', padding: '6px 12px', borderRadius: '8px' }}>
              <Calendar size={14} /> {filterOptions.find(f => f.id === period)?.label}
            </div>
          </div>

          <div style={{ height: '240px', display: 'flex', alignItems: 'flex-end', gap: chartBuckets.length > 8 ? '0.8rem' : '1.5rem', padding: '0 0.5rem' }}>
            {chartHeights.map((h, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', height: '100%', justifyContent: 'flex-end' }}>
                <span style={{ fontSize: '0.7rem', fontWeight: '800', color: '#084734' }}>
                  {chartBuckets[i].revenue > 0 ? `₹${chartBuckets[i].revenue >= 1000 ? `${(chartBuckets[i].revenue/1000).toFixed(1)}k` : chartBuckets[i].revenue}` : ''}
                </span>
                <div style={{ width: '100%', background: '#E8F5D0', borderRadius: '8px 8px 0 0', height: `${h}%`, position: 'relative', overflow: 'hidden', minHeight: '6px', transition: 'height 0.3s ease' }}>
                  <div style={{ position: 'absolute', bottom: 0, width: '100%', background: '#CEF17B', height: '40%', borderRadius: '8px 8px 0 0' }}></div>
                </div>
                <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '60px', textAlign: 'center' }}>
                  {chartBuckets[i].label}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: 'white', padding: '2rem', borderRadius: '24px', border: '1.5px solid #EEF2E6' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: '800', marginBottom: '2rem' }}>Revenue Breakdown</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {breakdown.map((b, i) => (
              <div key={i}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: '700', color: '#374151' }}>{b.label}</span>
                  <span style={{ fontSize: '0.9rem', fontWeight: '800', color: '#161616' }}>Rs.{b.value.toLocaleString()}</span>
                </div>
                <div style={{ height: '8px', background: '#EEF2E6', borderRadius: '10px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', background: b.color, width: `${Math.round((b.value / totalBreakdown) * 100)}%`, transition: 'width 0.3s ease' }}></div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: '2.5rem', padding: '1.5rem', borderRadius: '16px', border: '1.5px solid #EEF2E6', background: '#F8FAF7' }}>
            <h4 style={{ fontSize: '0.9rem', fontWeight: '800', marginBottom: '8px', color: '#084734' }}>Payout Cycle</h4>
            <p style={{ fontSize: '0.82rem', color: '#374151', fontWeight: '500', lineHeight: 1.5, margin: 0 }}>
              Estimated payout of <strong style={{ color: '#161616' }}>Rs.{Math.round(totalEarnings).toLocaleString('en-IN')}</strong> is scheduled for <strong style={{ color: '#161616' }}>{nextPayoutDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</strong>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const statCard = { background: 'white', padding: '1.5rem', borderRadius: '24px', border: '1.5px solid #EEF2E6' };
const btnPrimary = { background: '#CEF17B', color: '#084734', border: 'none', padding: '10px 20px', borderRadius: '12px', fontWeight: '800', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', boxShadow: '0 4px 14px rgba(8,71,52,0.1)' };
const btnSecondary = { background: '#F4F6F3', color: '#374151', border: '1px solid #E5E7EB', padding: '10px 14px', borderRadius: '12px', fontWeight: '700', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' };
